/**
 * InferMed MUI Theme Configuration
 *
 * This theme maps all design system tokens to Material-UI components,
 * ensuring visual consistency across the application.
 *
 * Color Palette:
 * - Primary (Teal): #008080 main
 * - Secondary (Navy): #003366 main
 * - Accent (Lime): #28a428 main
 * - Neutral: Gray scale 50-950
 *
 * Typography:
 * - Display: Montserrat (headings, UI text)
 * - Body: Roboto (body text, descriptions)
 */

import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  // ─────────────────────────────────────────────────────────────────
  // PALETTE: Color definitions mapping to design system tokens
  // ─────────────────────────────────────────────────────────────────
  palette: {
    primary: {
      main: '#0A3D38', // Abyssal Teal
      light: '#3B6460',
      dark: '#072B27',
      contrastText: '#F4EEE3',
    },
    secondary: {
      main: '#5DAF96', // Malachite Jade
      light: '#7DBFAC',
      dark: '#417A69',
      contrastText: '#0A3D38',
    },
    success: {
      main: '#5DAF96',
      contrastText: '#ffffff',
    },
    error: {
      main: '#dc2626',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#E8923A', // Insight Amber
      contrastText: '#ffffff',
    },
    info: {
      main: '#0A3D38',
      contrastText: '#F4EEE3',
    },
    neutral: {
      0: '#F4EEE3', // Textbook Ivory
      50: '#F4EEE3',
      100: '#EAE2D4', // Ivory Dim
      200: '#D5CEC0',
      300: '#BDB6A8',
      400: '#A59F91',
      500: '#8D877A',
      600: '#757063',
      700: '#5D584C',
      800: '#454135',
      900: '#2D291E',
      950: '#151107',
    },
    background: {
      default: '#F4EEE3',
      paper: '#ffffff',
    },
    text: {
      primary: '#1E2D3D', // Clinical Slate
      secondary: '#3A5068', // Slate Mid
      disabled: '#A59F91',
    },
    divider: '#EAE2D4',
    action: {
      active: '#0A3D38',
      hover: 'rgba(10, 61, 56, 0.04)',
      selected: 'rgba(10, 61, 56, 0.08)',
      disabled: '#D5CEC0',
      disabledBackground: '#EAE2D4',
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // TYPOGRAPHY: Font definitions and variants
  // ─────────────────────────────────────────────────────────────────
  typography: {
    fontFamily: '"DM Sans", "Helvetica", "Arial", sans-serif',

    h1: {
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: '58px',
      fontWeight: 300,
      lineHeight: 1.05,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: '42px',
      fontWeight: 300,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: '28px',
      fontWeight: 500,
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    h4: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.5,
    },

    body1: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '16px',
      fontWeight: 300,
      lineHeight: 1.75,
    },
    body2: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '14px',
      fontWeight: 300,
      lineHeight: 1.75,
    },

    subtitle1: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    subtitle2: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.5,
    },

    caption: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '12px',
      fontWeight: 300,
      lineHeight: 1.4,
    },
    overline: {
      fontFamily: '"DM Mono", monospace',
      fontSize: '11px',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
    },

    button: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.5,
      textTransform: 'none',
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // SPACING: Material-UI spacing scale
  // ─────────────────────────────────────────────────────────────────
  spacing: (factor) => `${factor * 4}px`, // MUI uses 8px by default, we're using 4px base

  // ─────────────────────────────────────────────────────────────────
  // SHAPE: Border radius configuration
  // ─────────────────────────────────────────────────────────────────
  shape: {
    borderRadius: 8, // Default radius-md
  },

  // ─────────────────────────────────────────────────────────────────
  // BREAKPOINTS: Responsive breakpoints
  // ─────────────────────────────────────────────────────────────────
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1400,
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // COMPONENT OVERRIDES: Theme customization for MUI components
  // ─────────────────────────────────────────────────────────────────
  components: {
    // Button styling
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '4px',
          padding: '10px 22px',
          transition: 'all 120ms cubic-bezier(0.16, 1, 0.3, 1)',
        },
        containedPrimary: {
          backgroundColor: '#E8923A',
          color: '#0A3D38',
          fontWeight: 700,
          '&:hover': {
            backgroundColor: '#cf7d2e',
            transform: 'translateY(-1px)',
          },
        },
        containedSecondary: {
          backgroundColor: '#0A3D38',
          '&:hover': {
            backgroundColor: '#072B27',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: 'rgba(10, 61, 56, 0.2)',
          '&:hover': {
            borderWidth: '1px',
            backgroundColor: 'rgba(10, 61, 56, 0.04)',
          },
        },
      },
    },

    // TextField/Input styling
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            backgroundColor: '#ffffff',
            '& fieldset': {
              borderColor: 'rgba(30, 45, 61, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: '#0A3D38',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#0A3D38',
              borderWidth: '1px',
            },
          },
        },
      },
    },

    // Card styling
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          boxShadow: '0 4px 24px rgba(10, 61, 56, 0.08)',
          border: '1px solid #EAE2D4',
        },
      },
    },

    // Tabs styling
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #EAE2D4',
          minHeight: '48px',
        },
        indicator: {
          backgroundColor: '#E8923A', // Insight Amber for emphasis
          height: '2px',
        },
      },
    },

    // Tab styling
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '14px',
          color: '#3A5068',
          '&:hover': {
            color: '#0A3D38',
          },
          '&.Mui-selected': {
            color: '#0A3D38',
          },
        },
      },
    },

    // Typography styling
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#1E2D3D',
        },
      },
    },

    // List styling
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          borderBottom: '1px solid #EAE2D4',
          '&:last-child': {
            borderBottom: 'none',
          },
          '&:hover': {
            backgroundColor: 'rgba(93, 175, 150, 0.05)',
          },
        },
      },
    },

    // Accordion styling
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          border: '1px solid #EAE2D4',
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: '#F4EEE3',
          '&:hover': {
            backgroundColor: '#EAE2D4',
          },
        },
      },
    },

    // Drawer styling
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--glass-nav-bg)',
          backdropFilter: 'blur(var(--glass-nav-blur))',
          WebkitBackdropFilter: 'blur(var(--glass-nav-blur))',
          borderRight: '1px solid var(--glass-nav-border)',
          color: '#F4EEE3',
        },
      },
    },

    // AppBar styling
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--glass-nav-bg-scrolled)',
          backdropFilter: 'blur(var(--glass-nav-blur))',
          WebkitBackdropFilter: 'blur(var(--glass-nav-blur))',
          boxShadow: 'var(--glass-nav-shadow)',
          borderBottom: '1px solid var(--glass-nav-border)',
        },
      },
    },

    // Chip styling
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '99px',
          fontWeight: 500,
          fontSize: '11px',
        },
        filled: {
          backgroundColor: 'rgba(93, 175, 150, 0.15)',
          color: '#5DAF96',
          border: '1px solid rgba(93, 175, 150, 0.3)',
        },
        colorPrimary: {
          backgroundColor: 'rgba(93, 175, 150, 0.15)',
          color: '#5DAF96',
        },
        colorSecondary: {
          backgroundColor: 'rgba(232, 146, 58, 0.15)',
          color: '#E8923A',
        },
      },
    },

    // Alert styling
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          border: 'none',
        },
        standardInfo: {
          backgroundColor: 'rgba(10, 61, 56, 0.05)',
          color: '#0A3D38',
        },
        standardSuccess: {
          backgroundColor: 'rgba(93, 175, 150, 0.1)',
          color: '#5DAF96',
        },
        standardWarning: {
          backgroundColor: 'rgba(232, 146, 58, 0.1)',
          color: '#E8923A',
        },
      },
    },

    // Paper styling
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
        },
      },
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // TRANSITIONS: Animation configuration
  // ─────────────────────────────────────────────────────────────────
  transitions: {
    duration: {
      shortest: 120, // duration-fast
      shorter: 220,  // duration-normal
      short: 220,
      standard: 220,
      complex: 380,  // duration-slow
    },
    easing: {
      easeInOut: 'cubic-bezier(0.16, 1, 0.3, 1)', // ease-out
      easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      linear: 'linear',
      sharp: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // Z-INDEX: Stacking order
  // ─────────────────────────────────────────────────────────────────
  zIndex: {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    fab: 1050,
    snackbar: 1400,
    tooltip: 1300,
    modal: 1300,
    popover: 1300,
  },
});

export default theme;
